import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Send, Clock, CheckCircle, XCircle, Instagram, Facebook, ExternalLink, Store, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLanguage } from "../lib/language";

// Vendor application form ("Ser Vendedor" tab in Profile).
// Same UX as BecomePartnerPanel but with store-specific fields.
export default function BecomeVendorPanel({ user }) {
    const { t } = useLanguage();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/vendor-applications/mine");
            setApplications(data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const latest = applications[0];
    const canReapply = !latest || latest.status !== "pending";

    if (!user.verified) {
        return (
            <div className="tb-card p-6 text-center space-y-3" data-testid="become-vendor-gate">
                <BadgeCheck className="w-10 h-10 text-[#2196F3] mx-auto" />
                <h3 className="font-display text-xl">{t("Verifica tu identidad primero", "Verify your identity first")}</h3>
                <p className="text-sm text-[var(--tb-text-2)] max-w-md mx-auto">
                    {t(
                        "Para postularte como vendedor necesitas tener el check azul. Sube una selfie y una foto de tu documento desde la pestaña de verificación.",
                        "To apply as a seller you need the blue check. Upload a selfie and an ID photo from the verification tab."
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="become-vendor-panel">
            <div className="tb-card p-5">
                <h3 className="font-display text-xl mb-2 inline-flex items-center gap-2">
                    <Store className="w-5 h-5 text-[var(--tb-gold)]" />
                    {t("Vender en Terra Boutique", "Sell on Terra Boutique")}
                </h3>
                <p className="text-sm text-[var(--tb-text-2)] leading-relaxed">
                    {t(
                        "Como vendedor puedes listar tus propios productos en Terra Boutique. El equipo revisará tu solicitud y al aprobarla, definirá el modelo de cobro (comisión por venta o suscripción mensual) que mejor te acomode.",
                        "As a seller you can list your own products on Terra Boutique. The team will review your application and upon approval, set the billing model (per-sale commission or monthly subscription) that fits you best."
                    )}
                </p>
            </div>

            {loading ? (
                <div className="text-xs text-[var(--tb-muted)]">{t("Cargando…", "Loading…")}</div>
            ) : latest && (
                <VendorApplicationStatusCard app={latest} />
            )}

            {canReapply && <VendorApplicationForm onSubmitted={load} />}
        </div>
    );
}

function VendorApplicationStatusCard({ app }) {
    const { t } = useLanguage();
    const cfg = {
        pending: { icon: Clock, tone: "bg-amber-500/15 border-amber-400/40 text-amber-300",
                   label: t("Solicitud en revisión", "Application under review") },
        approved: { icon: CheckCircle, tone: "bg-emerald-500/15 border-emerald-400/40 text-emerald-300",
                    label: t("Aprobada", "Approved") },
        rejected: { icon: XCircle, tone: "bg-rose-500/15 border-rose-400/40 text-rose-300",
                    label: t("Rechazada", "Rejected") },
    }[app.status] || { icon: Clock, tone: "bg-[var(--tb-bg-2)] border-[var(--tb-border)] text-[var(--tb-muted)]", label: app.status };
    const Icon = cfg.icon;
    return (
        <div className={`tb-card p-5 border ${cfg.tone}`} data-testid={`vendor-app-status-${app.status}`}>
            <div className="flex items-center gap-3 mb-2">
                <Icon className="w-5 h-5" />
                <div className="font-display text-lg">{cfg.label}</div>
                <div className="text-[10px] opacity-70 ml-auto">{new Date(app.created_at).toLocaleDateString("es-SV")}</div>
            </div>
            <div className="text-xs opacity-90 space-y-1">
                <div><strong>{t("Tienda:", "Store:")}</strong> {app.store_name}</div>
                <div className="italic">&ldquo;{app.store_description}&rdquo;</div>
                {app.admin_note && (
                    <div className="mt-2 pt-2 border-t border-current/20">
                        <strong>{t("Nota del equipo:", "Team note:")}</strong> {app.admin_note}
                    </div>
                )}
            </div>
        </div>
    );
}

const CATEGORY_OPTIONS = [
    "Bolsos", "Ropa", "Joyería", "Relojes", "Zapatos", "Accesorios",
    "Belleza", "Perfumería", "Tecnología", "Hogar", "Otro",
];

function VendorApplicationForm({ onSubmitted }) {
    const { t } = useLanguage();
    // Store info
    const [storeName, setStoreName] = useState("");
    const [storeDescription, setStoreDescription] = useState("");
    const [categories, setCategories] = useState([]);
    const [previousExperience, setPreviousExperience] = useState("");
    const [expectedVolume, setExpectedVolume] = useState("");
    const [motivation, setMotivation] = useState("");
    const [samplePhotos, setSamplePhotos] = useState([""]);
    // Contact
    const [phone, setPhone] = useState("");
    const [instagram, setInstagram] = useState("");
    const [tiktok, setTiktok] = useState("");
    const [facebook, setFacebook] = useState("");
    // Bank
    const [bankName, setBankName] = useState("");
    const [bankType, setBankType] = useState("Ahorros");
    const [bankNumber, setBankNumber] = useState("");
    const [bankHolder, setBankHolder] = useState("");
    // Address (SV structured — same catalog endpoints as Checkout / BecomePartner)
    const [addressStreet, setAddressStreet] = useState("");
    const [addressReference, setAddressReference] = useState("");
    const [departamentoId, setDepartamentoId] = useState("");
    const [municipioId, setMunicipioId] = useState("");
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [loadingDeptos, setLoadingDeptos] = useState(false);
    const [loadingMunis, setLoadingMunis] = useState(false);
    // ID
    const [documentType, setDocumentType] = useState("DUI");
    const [documentNumber, setDocumentNumber] = useState("");
    const [saving, setSaving] = useState(false);

    const loadDepartamentos = useCallback(async () => {
        setLoadingDeptos(true);
        try {
            const { data } = await api.get("/c807-public/catalogo/departamentos");
            const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setDepartamentos(list);
        } catch { setDepartamentos([]); }
        finally { setLoadingDeptos(false); }
    }, []);
    useEffect(() => { loadDepartamentos(); }, [loadDepartamentos]);

    const onPickDepartamento = async (raw) => {
        const depId = raw === "" || raw == null ? "" : Number(raw);
        setDepartamentoId(depId);
        setMunicipioId("");
        setMunicipios([]);
        if (!depId || Number.isNaN(depId)) return;
        setLoadingMunis(true);
        try {
            const { data } = await api.get(`/c807-public/catalogo/municipios?departamento=${depId}`);
            const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setMunicipios(list);
        } catch {
            setMunicipios([]);
            toast.error(t("No se pudo cargar la lista de distritos", "Could not load districts"));
        } finally { setLoadingMunis(false); }
    };

    const toggleCategory = (cat) => {
        setCategories((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]);
    };

    const addPhotoField = () => samplePhotos.length < 5 && setSamplePhotos([...samplePhotos, ""]);
    const removePhotoField = (idx) => setSamplePhotos(samplePhotos.filter((_, i) => i !== idx));
    const updatePhotoField = (idx, val) => setSamplePhotos(samplePhotos.map((p, i) => (i === idx ? val : p)));

    const submit = async (e) => {
        e.preventDefault();
        if (storeName.trim().length < 2) { toast.error(t("Ingresa el nombre de tu tienda", "Enter your store name")); return; }
        if (storeDescription.trim().length < 20) { toast.error(t("Describe qué vendes con al menos 20 caracteres", "Describe what you sell in at least 20 characters")); return; }
        if (!addressStreet.trim()) { toast.error(t("Ingresa tu calle y número", "Enter your street and number")); return; }
        if (!departamentoId) { toast.error(t("Selecciona tu departamento", "Select your department")); return; }
        if (!municipioId) { toast.error(t("Selecciona tu municipio", "Select your municipality")); return; }
        if (!documentNumber.trim() || documentNumber.trim().length < 5) { toast.error(t("Ingresa un número de documento válido", "Enter a valid document number")); return; }
        setSaving(true);
        try {
            const social_links = {};
            if (instagram.trim()) social_links.instagram = instagram.trim();
            if (tiktok.trim()) social_links.tiktok = tiktok.trim();
            if (facebook.trim()) social_links.facebook = facebook.trim();
            await api.post("/vendor-applications", {
                store_name: storeName,
                store_description: storeDescription,
                categories,
                previous_experience: previousExperience,
                expected_monthly_volume: expectedVolume,
                motivation,
                sample_photos: samplePhotos.filter((u) => u.trim()),
                contact_phone: phone,
                social_links,
                bank_name: bankName,
                bank_account_type: bankType,
                bank_account_number: bankNumber,
                bank_account_holder: bankHolder,
                address_street: addressStreet,
                departamento_id: departamentoId || null,
                municipio_id: municipioId || null,
                address_reference: addressReference,
                document_type: documentType,
                document_number: documentNumber,
            });
            toast.success(t("¡Solicitud enviada! Te avisaremos por email.", "Application submitted! We'll email you."));
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("Error al enviar", "Error submitting"));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="tb-card p-5 space-y-4" data-testid="become-vendor-form">
            <h3 className="font-display text-lg">{t("Solicítalo ahora", "Apply now")}</h3>

            {/* Store */}
            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("Nombre de tu tienda o marca *", "Store or brand name *")}</label>
                <input className="tb-input mt-1" value={storeName} onChange={(e) => setStoreName(e.target.value)}
                    placeholder={t("Ej. Boutique Luna", "E.g. Luna Boutique")} maxLength={80} required
                    data-testid="vendor-store-name" />
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("¿Qué vendes? *", "What do you sell? *")}</label>
                <textarea className="tb-input mt-1 min-h-[80px]" value={storeDescription}
                    onChange={(e) => setStoreDescription(e.target.value)}
                    placeholder={t("Describe brevemente qué tipo de productos venderás en Terra Boutique...",
                                    "Briefly describe what you plan to sell on Terra Boutique...")}
                    minLength={20} required data-testid="vendor-description" />
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("Categorías principales", "Main categories")}</label>
                <div className="mt-2 flex flex-wrap gap-2">
                    {CATEGORY_OPTIONS.map((cat) => (
                        <button key={cat} type="button" onClick={() => toggleCategory(cat)}
                            className={`text-[11px] px-3 py-1 rounded border transition ${categories.includes(cat)
                                ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/20 text-[var(--tb-gold)]"
                                : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/50"}`}
                            data-testid={`vendor-cat-${cat.toLowerCase()}`}>
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">{t("Experiencia previa vendiendo", "Previous selling experience")}</label>
                    <input className="tb-input mt-1" value={previousExperience} onChange={(e) => setPreviousExperience(e.target.value)}
                        placeholder={t("Ej. 2 años en Instagram", "E.g. 2 years on Instagram")}
                        data-testid="vendor-experience" />
                </div>
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">{t("Volumen esperado mensual", "Expected monthly volume")}</label>
                    <input className="tb-input mt-1" value={expectedVolume} onChange={(e) => setExpectedVolume(e.target.value)}
                        placeholder={t("Ej. $500–$1,000 USD", "E.g. $500–$1,000 USD")}
                        data-testid="vendor-volume" />
                </div>
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("¿Por qué quieres vender aquí?", "Why do you want to sell here?")}</label>
                <textarea className="tb-input mt-1 min-h-[60px]" value={motivation} onChange={(e) => setMotivation(e.target.value)}
                    placeholder={t("Cuéntanos tu motivación (opcional)", "Tell us your motivation (optional)")}
                    data-testid="vendor-motivation" />
            </div>

            {/* Sample photos */}
            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("URLs de fotos de productos (opcional, máx 5)", "Product photo URLs (optional, max 5)")}</label>
                <div className="mt-1 space-y-2">
                    {samplePhotos.map((url, idx) => (
                        <div key={idx} className="flex gap-1">
                            <input className="tb-input flex-1" value={url} onChange={(e) => updatePhotoField(idx, e.target.value)}
                                placeholder="https://..." data-testid={`vendor-photo-${idx}`} />
                            {samplePhotos.length > 1 && (
                                <button type="button" onClick={() => removePhotoField(idx)} className="tb-btn-ghost !p-2">
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                    {samplePhotos.length < 5 && (
                        <button type="button" onClick={addPhotoField} className="tb-btn-secondary !text-xs inline-flex items-center gap-1">
                            <Plus className="w-3 h-3" /> {t("Añadir otra foto", "Add another photo")}
                        </button>
                    )}
                </div>
            </div>

            {/* Contact */}
            <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-[var(--tb-border)]">
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">{t("Teléfono de contacto", "Contact phone")}</label>
                    <input className="tb-input mt-1" value={phone} onChange={(e) => setPhone(e.target.value)}
                        placeholder="+503 ..." data-testid="vendor-phone" />
                </div>
            </div>

            <div>
                <label className="tb-overline text-[var(--tb-gold)]">{t("Redes sociales (opcional)", "Social media (optional)")}</label>
                <div className="grid sm:grid-cols-3 gap-2 mt-1">
                    <div className="relative">
                        <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)]" />
                        <input className="tb-input !pl-9" placeholder="@usuario" value={instagram} onChange={(e) => setInstagram(e.target.value)} data-testid="vendor-instagram" />
                    </div>
                    <div className="relative">
                        <ExternalLink className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)]" />
                        <input className="tb-input !pl-9" placeholder="TikTok" value={tiktok} onChange={(e) => setTiktok(e.target.value)} data-testid="vendor-tiktok" />
                    </div>
                    <div className="relative">
                        <Facebook className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)]" />
                        <input className="tb-input !pl-9" placeholder="Facebook" value={facebook} onChange={(e) => setFacebook(e.target.value)} data-testid="vendor-facebook" />
                    </div>
                </div>
            </div>

            {/* Address (identical block to BecomePartner) */}
            <div className="pt-3 border-t border-[var(--tb-border)]">
                <label className="tb-overline text-[var(--tb-gold)]">{t("Dirección (para envíos administrativos)", "Address (for administrative shipments)")}</label>
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 mb-2 flex items-center gap-1">
                    🔒 {t("Solo el equipo administrativo ve esta información.", "Only the admin team sees this information.")}
                </p>
                <div className="space-y-3">
                    <div>
                        <label className="tb-overline text-[10px]">{t("Calle y número *", "Street and number *")}</label>
                        <input className="tb-input mt-1" value={addressStreet} onChange={(e) => setAddressStreet(e.target.value)}
                            placeholder={t("Ej. Calle Principal #123", "E.g. 123 Main Street")} maxLength={140}
                            data-testid="vendor-address-street" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline text-[10px]">{t("Departamento *", "Department *")}</label>
                            <select className="tb-input mt-1"
                                value={departamentoId === "" ? "" : String(departamentoId)}
                                onChange={(e) => onPickDepartamento(e.target.value)}
                                disabled={loadingDeptos}
                                data-testid="vendor-departamento" required>
                                <option value="">{loadingDeptos ? t("Cargando...", "Loading...") : t("Selecciona...", "Select...")}</option>
                                {departamentos.map((d) => <option key={d.id} value={String(d.id)}>{d.nombre}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="tb-overline text-[10px]">{t("Municipio *", "Municipality *")}</label>
                            <select className="tb-input mt-1"
                                value={municipioId === "" ? "" : String(municipioId)}
                                onChange={(e) => setMunicipioId(e.target.value === "" ? "" : Number(e.target.value))}
                                disabled={loadingMunis || !departamentoId || municipios.length === 0}
                                data-testid="vendor-municipio" required>
                                <option value="">
                                    {!departamentoId ? t("Elige departamento", "Choose department")
                                        : loadingMunis ? t("Cargando...", "Loading...")
                                        : municipios.length === 0 ? t("Sin distritos", "No districts")
                                        : t("Selecciona...", "Select...")}
                                </option>
                                {municipios.map((m) => <option key={m.id} value={String(m.id)}>{m.nombre}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Puntos de referencia", "Landmarks")} <span className="text-[var(--tb-muted)] normal-case tracking-normal">({t("opcional", "optional")})</span></label>
                        <textarea className="tb-input mt-1 min-h-[50px]"
                            value={addressReference} onChange={(e) => setAddressReference(e.target.value)}
                            maxLength={240} data-testid="vendor-address-reference" />
                    </div>
                </div>
            </div>

            {/* ID Document */}
            <div className="pt-3 border-t border-[var(--tb-border)]">
                <label className="tb-overline text-[var(--tb-gold)]">{t("Documento de identidad *", "Identity document *")}</label>
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 mb-2">🔒 {t("Requerido para pagos y auditoría.", "Required for payments and audit.")}</p>
                <div className="grid sm:grid-cols-[140px_1fr] gap-3">
                    <div>
                        <label className="tb-overline text-[10px]">{t("Tipo", "Type")}</label>
                        <select className="tb-input mt-1" value={documentType} onChange={(e) => setDocumentType(e.target.value)} data-testid="vendor-doc-type">
                            <option value="DUI">DUI</option>
                            <option value="Pasaporte">{t("Pasaporte", "Passport")}</option>
                        </select>
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Número", "Number")}</label>
                        <input className="tb-input mt-1 font-mono uppercase" value={documentNumber}
                            onChange={(e) => setDocumentNumber(e.target.value.toUpperCase())}
                            placeholder={documentType === "DUI" ? "00000000-0" : "A12345678"}
                            maxLength={30} required data-testid="vendor-doc-number" />
                    </div>
                </div>
            </div>

            {/* Bank */}
            <div className="pt-3 border-t border-[var(--tb-border)]">
                <label className="tb-overline text-[var(--tb-gold)]">{t("Datos bancarios", "Bank details")}</label>
                <p className="text-[10px] text-[var(--tb-muted)] mt-1 mb-2">🔒 {t("Se usa para transferirte comisiones o reembolsos.", "Used to transfer commissions or refunds.")}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="tb-overline text-[10px]">{t("Banco", "Bank")}</label>
                        <input className="tb-input mt-1" value={bankName} onChange={(e) => setBankName(e.target.value)}
                            maxLength={60} data-testid="vendor-bank-name" />
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Tipo de cuenta", "Account type")}</label>
                        <select className="tb-input mt-1" value={bankType} onChange={(e) => setBankType(e.target.value)} data-testid="vendor-bank-type">
                            <option value="Ahorros">{t("Ahorros", "Savings")}</option>
                            <option value="Corriente">{t("Corriente", "Checking")}</option>
                        </select>
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Número de cuenta", "Account number")}</label>
                        <input className="tb-input mt-1 font-mono" value={bankNumber} onChange={(e) => setBankNumber(e.target.value)}
                            maxLength={40} data-testid="vendor-bank-number" />
                    </div>
                    <div>
                        <label className="tb-overline text-[10px]">{t("Titular de la cuenta", "Account holder")}</label>
                        <input className="tb-input mt-1" value={bankHolder} onChange={(e) => setBankHolder(e.target.value)}
                            maxLength={80} data-testid="vendor-bank-holder" />
                    </div>
                </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-[var(--tb-border)]">
                <button type="submit" disabled={saving} className="tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="vendor-submit">
                    <Send className="w-4 h-4" /> {saving ? t("Enviando…", "Submitting…") : t("Enviar solicitud", "Submit application")}
                </button>
            </div>
        </form>
    );
}
